import type { Dimensions, Viewport, XYPosition } from '@xyflow/react';
import { createContext, type PropsWithChildren, useContext, useMemo, useRef, useState } from 'react';
import { useMatch } from 'react-router';

import { useAppDispatch } from '../../../configuration/setup/hooks';
import { routes } from '../../../routes/routes';
import {
    controlTowerApi,
    type OverviewQuery,
    type Transport,
    type TransportRequest,
} from '../../../services/controlTowerApi';
import { buildLiveGraph } from './liveGraph';
import { useLiveGraphRequestsQuery, useLiveGraphTransportsQuery } from './liveGraphApi';

const initialQuery: OverviewQuery = {
    search: '',
    plantIds: [],
    carriers: [],
    sort: 'order_number',
    direction: 'asc',
    offset: 0,
    limit: 50,
};

export type LiveGraphFixture = { requests: TransportRequest[]; transports: Transport[] };

const useLiveGraphState = (fixture?: LiveGraphFixture) => {
    const dispatch = useAppDispatch();
    const active = Boolean(useMatch(routes.GRAPH));
    const [query, setQuery] = useState(initialQuery);
    const [transportOffset, setTransportOffset] = useState(0);
    const [selection, setSelection] = useState<string | null>(null);
    const positions = useRef(new Map<string, XYPosition>());
    const dimensions = useRef(new Map<string, Dimensions>());
    const viewport = useRef<Viewport | null>(null);
    const options = {
        skip: !active || Boolean(fixture),
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
        refetchOnMountOrArgChange: true,
    };
    const requests = useLiveGraphRequestsQuery(query, {
        ...options,
        pollingInterval: query.offset === 0 ? 5000 : 0,
    });
    const transports = useLiveGraphTransportsQuery(
        { ...query, offset: transportOffset },
        { ...options, pollingInterval: transportOffset === 0 ? 5000 : 0 }
    );
    const previewPages = useMemo(() => {
        if (!fixture) {
            return;
        }
        const plants = fixture.requests.map(request => ({
            id: request.ship_to.id ?? '',
            label: request.ship_to.address.name,
        }));
        const carriers = [
            ...new Set(
                fixture.transports.flatMap(transport => (transport.carrier ? [transport.carrier.carrier_name] : []))
            ),
        ].map(name => ({ id: name, label: name }));
        const page = <T,>(items: T[]) => ({
            items,
            total: items.length,
            unfiltered_total: items.length,
            offset: 0,
            limit: 50,
            facets: { plants, carriers },
        });
        return { requests: page(fixture.requests), transports: page(fixture.transports) };
    }, [fixture]);
    const requestData = previewPages?.requests ?? requests.currentData;
    const transportData = previewPages?.transports ?? transports.currentData;
    const graph = useMemo(
        () => buildLiveGraph(requestData?.items ?? [], transportData?.items ?? []),
        [requestData, transportData]
    );
    const plants = [...(requestData?.facets.plants ?? []), ...(transportData?.facets.plants ?? [])];
    const updateQuery = (patch: Partial<OverviewQuery>) => {
        setQuery(current => ({ ...current, ...patch, offset: 0 }));
        setTransportOffset(0);
        setSelection(null);
    };
    return {
        query,
        preview: Boolean(fixture),
        updateQuery,
        graph,
        selection,
        setSelection,
        positions,
        dimensions,
        viewport,
        refreshAfterSignal: () => {
            setQuery(current => ({ ...current, offset: 0 }));
            setTransportOffset(0);
            setSelection(null);
            dispatch(controlTowerApi.util.invalidateTags(['TransportData']));
        },
        facets: {
            plants: [...new Map(plants.map(option => [option.id, option])).values()].sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
            carriers: transportData?.facets.carriers ?? [],
        },
        fetching: requests.isFetching || transports.isFetching,
        initialLoading: (requests.isFetching || transports.isFetching) && (!requestData || !transportData),
        error: requests.isError || transports.isError,
        hasData: (requestData?.unfiltered_total ?? 0) + (transportData?.unfiltered_total ?? 0) > 0,
        hasMoreRequests: (requestData?.items.length ?? 0) < (requestData?.total ?? 0),
        hasMoreTransports: (transportData?.items.length ?? 0) < (transportData?.total ?? 0),
        loadMoreRequests: () =>
            setQuery(current => ({ ...current, offset: (requestData?.offset ?? 0) + (requestData?.limit ?? 50) })),
        loadMoreTransports: () => setTransportOffset((transportData?.offset ?? 0) + (transportData?.limit ?? 50)),
        reset: () => {
            setQuery(initialQuery);
            setTransportOffset(0);
            setSelection(null);
        },
        retry: () => {
            if (query.offset !== 0 || transportOffset !== 0) {
                updateQuery({});
            } else {
                requests.refetch();
                transports.refetch();
            }
        },
    };
};

const LiveGraphContext = createContext<ReturnType<typeof useLiveGraphState> | null>(null);

export const LiveGraphProvider = ({ children, fixture }: PropsWithChildren<{ fixture?: LiveGraphFixture }>) => {
    const state = useLiveGraphState(fixture);
    return <LiveGraphContext.Provider value={state}>{children}</LiveGraphContext.Provider>;
};

export const useLiveGraph = () => {
    const context = useContext(LiveGraphContext);
    if (!context) {
        throw new Error('LiveGraphProvider is missing');
    }
    return context;
};
